import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Chip,
    useMediaQuery,
    useTheme,
    Tabs,
    Tab,
    CircularProgress,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Divider,
    tooltipClasses,
    TooltipProps,
} from '@mui/material';
import { ArrowBack, Add, Edit, Delete, Person, CreditCard, ShoppingCart, Visibility, AccountBalance } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import api from '../../api/axios';

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
    <div hidden={value !== index}>{value === index && <Box sx={{ pt: 3 }}>{children}</Box>}</div>
);

const TenantDetail = () => {
    const { tenantId } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const queryClient = useQueryClient();

    const [tabValue, setTabValue] = useState(0);
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
    const [cartAddonDialogOpen, setCartAddonDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [selectedSubscription, setSelectedSubscription] = useState<any>(null);
    const [error, setError] = useState('');
    const [cartAddonForm, setCartAddonForm] = useState({ addon_id: '', quantity: 1 });

    // Delete dialog states
    const [deleteUserDialogOpen, setDeleteUserDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<any>(null);
    const [deleteSubscriptionDialogOpen, setDeleteSubscriptionDialogOpen] = useState(false);
    const [subscriptionToDelete, setSubscriptionToDelete] = useState<any>(null);

    // User form
    const [userForm, setUserForm] = useState({ name: '', phone: '', email: '', role: 'sub_admin' });
    // Subscription form
    const getDefaultSubForm = () => ({
        plan_id: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        max_citizens: 1000,
        max_users: 5,
        max_storage: 1024,
        is_active: true,
        has_cms_access: false,
        has_campaign_access: false,
    });
    const [subForm, setSubForm] = useState(getDefaultSubForm());

    // Fetch tenant details
    const { data: tenantData, isLoading: tenantLoading } = useQuery({
        queryKey: ['tenant', tenantId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}`);
            return res.data?.data;
        },
    });

    // Fetch tenant users
    const { data: usersData, isLoading: usersLoading } = useQuery({
        queryKey: ['tenant-users', tenantId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}/users`);
            return res.data?.data;
        },
    });

    // Fetch tenant subscriptions
    const { data: subscriptionsData, isLoading: subscriptionsLoading } = useQuery({
        queryKey: ['tenant-subscriptions', tenantId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}/subscriptions`);
            return res.data?.data;
        },
    });

    // Fetch tenant cart
    const { data: cartData, isLoading: cartLoading } = useQuery({
        queryKey: ['tenant-cart', tenantId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}/cart`);
            return res.data?.data;
        },
    });

    // Fetch plans for subscription form
    const { data: plansData } = useQuery({
        queryKey: ['plans-list'],
        queryFn: async () => {
            const res = await api.get('/superadmin/plans', { params: { per_page: 100 } });
            return res.data?.data?.data || [];
        },
    });

    // Fetch addons for cart
    const { data: addonsData } = useQuery({
        queryKey: ['addons-list'],
        queryFn: async () => {
            const res = await api.get('/superadmin/addons', { params: { per_page: 100 } });
            return res.data?.data?.data?.filter((a: any) => a.is_active) || [];
        },
    });

    // User mutations
    const saveUserMutation = useMutation({
        mutationFn: async (data: any) => {
            if (selectedUser) {
                return api.put(`/superadmin/tenants/${tenantId}/users/${selectedUser.id}`, data);
            }
            return api.post(`/superadmin/tenants/${tenantId}/users`, data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] });
            closeUserDialog();
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    const deleteUserMutation = useMutation({
        mutationFn: async (userId: number) => api.delete(`/superadmin/tenants/${tenantId}/users/${userId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] }),
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    // Subscription mutations
    const saveSubscriptionMutation = useMutation({
        mutationFn: async (data: any) => {
            if (selectedSubscription) {
                return api.put(`/superadmin/tenants/${tenantId}/subscriptions/${selectedSubscription.id}`, data);
            }
            return api.post(`/superadmin/tenants/${tenantId}/subscriptions`, data);
        },
        onSuccess: (response) => {
            if (response.data?.status === 'error') {
                setError(response.data.message);
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['tenant-subscriptions', tenantId] });
            closeSubscriptionDialog();
        },
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    const deleteSubscriptionMutation = useMutation({
        mutationFn: async (subId: number) => api.delete(`/superadmin/tenants/${tenantId}/subscriptions/${subId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-subscriptions', tenantId] }),
        onError: (err: any) => setError(err.response?.data?.message || err.message),
    });

    const openUserDialog = (user?: any) => {
        if (user) {
            setSelectedUser(user);
            setUserForm({ name: user.name, phone: user.phone, email: user.email || '', role: user.role });
        } else {
            setSelectedUser(null);
            setUserForm({ name: '', phone: '', email: '', role: 'sub_admin' });
        }
        setError('');
        setUserDialogOpen(true);
    };

    const closeUserDialog = () => {
        setUserDialogOpen(false);
        setSelectedUser(null);
        setError('');
    };

    const openSubscriptionDialog = (sub?: any) => {
        if (sub) {
            setSelectedSubscription(sub);
            setSubForm({
                plan_id: sub.plan_id || '',
                start_date: sub.start_date?.split('T')[0] || '',
                end_date: sub.end_date?.split('T')[0] || '',
                max_citizens: sub.max_citizens || 1000,
                max_users: sub.max_users || 5,
                max_storage: sub.max_storage || 1024,
                is_active: sub.is_active ?? true,
                has_cms_access: sub.has_cms_access ?? false,
                has_campaign_access: sub.has_campaign_access ?? false,
            });
        } else {
            setSelectedSubscription(null);
            setSubForm(getDefaultSubForm());
        }
        setError('');
        setSubscriptionDialogOpen(true);
    };

    const closeSubscriptionDialog = () => {
        setSubscriptionDialogOpen(false);
        setSelectedSubscription(null);
        setError('');
    };

    if (tenantLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const userColumns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 70,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 180 },
        { field: 'phone', headerName: 'Phone', width: 130 },
        {
            field: 'role', headerName: 'Role', width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value === 'admin' ? 'Admin' : 'Sub Admin'} size="small"
                    color={params.value === 'admin' ? 'primary' : 'default'} />
            ),
        },
        {
            field: 'actions', headerName: 'Actions', width: 120, sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => openUserDialog(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => {
                                setUserToDelete(params.row);
                                setDeleteUserDialogOpen(true);
                            }}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    const subscriptionColumns: GridColDef[] = [
        {
            field: 'index',
            headerName: 'Sr. No',
            width: 70,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => params.api.getAllRowIds().indexOf(params.row.id) + 1,
        },
        { field: 'plan', headerName: 'Plan', flex: 1, valueGetter: (value: any) => value?.name || 'N/A' },
        { field: 'amount_paid', headerName: 'Amount', width: 100, valueFormatter: (value: number) => `₹${value}` },
        {
            field: 'is_active', headerName: 'Status', width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <Chip label={params.value ? 'Active' : 'Inactive'} size="small" color={params.value ? 'success' : 'default'} />
            ),
        },
        {
            field: 'expires_at', headerName: 'Expires', width: 120,
            valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A',
        },
        {
            field: 'actions', headerName: 'Actions', width: 180, sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'orange' }}>
                        <BootstrapTooltip title="View Addons" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => navigate(`/super-admin/tenants/${tenantId}/subscriptions/${params.row.id}`)}>
                                <Visibility fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'green' }}>
                        <BootstrapTooltip title="Edit" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => openSubscriptionDialog(params.row)}>
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                    <Button variant="contained" size="small" sx={{ mr: 1, textTransform: 'none', minWidth: 'auto', padding: '4px', backgroundColor: 'red' }}>
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton size="small" sx={{ color: '#ffff' }} onClick={() => {
                                setSubscriptionToDelete(params.row);
                                setDeleteSubscriptionDialogOpen(true);
                            }}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    </Button>
                </Box>
            ),
        },
    ];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <IconButton onClick={() => navigate('/super-admin/tenants')}><ArrowBack /></IconButton>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.5rem' } }}>{tenantData?.name}</Typography>
                    <Typography color="text.secondary" sx={{ wordBreak: 'break-word' }}>{tenantData?.email} • {tenantData?.phone}</Typography>
                </Box>
                <Box>
                    <Chip label={tenantData?.is_active ? 'Active' : 'Inactive'}
                        color={tenantData?.is_active ? 'success' : 'default'} />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Usage Overview */}
            {tenantData?.usage && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Usage Overview</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                            {/* Citizens Usage */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Citizens</Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {tenantData.usage.citizens.used} / {tenantData.usage.citizens.limit} ({tenantData.usage.citizens.percentage}%)
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(tenantData.usage.citizens.percentage, 100)}
                                    color={tenantData.usage.citizens.percentage > 90 ? 'error' : 'primary'}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {tenantData.usage.citizens.remaining} remaining
                                </Typography>
                            </Box>

                            {/* Storage Usage */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">Storage</Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {tenantData.usage.storage.used_mb} MB / {tenantData.usage.storage.limit_mb} MB ({tenantData.usage.storage.percentage}%)
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.min(tenantData.usage.storage.percentage, 100)}
                                    color={tenantData.usage.storage.percentage > 90 ? 'error' : 'primary'}
                                    sx={{ height: 8, borderRadius: 4 }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    {tenantData.usage.storage.remaining_mb} MB remaining
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Tabs */}
            <Card>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }} variant="scrollable" scrollButtons="auto">
                    <Tab icon={<Person />} label="Users" iconPosition="start" />
                    <Tab icon={<CreditCard />} label="Subscriptions" iconPosition="start" />
                    <Tab icon={<ShoppingCart />} label="Cart" iconPosition="start" />
                    <Tab icon={<AccountBalance />} label="Constituencies" iconPosition="start" />
                </Tabs>

                <CardContent>
                    {/* Users Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h4">Tenant Users</Typography>
                            <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openUserDialog()}>
                                Add User
                            </Button>
                        </Box>
                        <DataGrid
                            rows={usersData?.data || usersData || []}
                            columns={userColumns}
                            loading={usersLoading}
                            autoHeight
                            pageSizeOptions={[10, 25]}
                            disableRowSelectionOnClick
                            sx={{ border: 0 }}
                        />
                    </TabPanel>

                    {/* Subscriptions Tab */}
                    <TabPanel value={tabValue} index={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h4">Subscriptions</Typography>
                            <Button variant="contained" size="small" startIcon={<Add />} onClick={() => openSubscriptionDialog()}>
                                Add Subscription
                            </Button>
                        </Box>
                        <DataGrid
                            rows={subscriptionsData?.data || subscriptionsData || []}
                            columns={subscriptionColumns}
                            loading={subscriptionsLoading}
                            autoHeight
                            pageSizeOptions={[10, 25]}
                            disableRowSelectionOnClick
                            sx={{ border: 0 }}
                        />
                    </TabPanel>

                    {/* Cart Tab */}
                    <TabPanel value={tabValue} index={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6">Shopping Cart</Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                {cartData && (
                                    <>
                                        <Button size="small" variant="outlined" color="warning"
                                            onClick={async () => {
                                                if (window.confirm('Clear all items from cart?')) {
                                                    await api.post(`/superadmin/tenants/${tenantId}/cart/clear`);
                                                    queryClient.invalidateQueries({ queryKey: ['tenant-cart', tenantId] });
                                                }
                                            }}>
                                            Clear Cart
                                        </Button>
                                        <Button size="small" variant="outlined" color="error"
                                            onClick={async () => {
                                                if (window.confirm('Delete the entire cart?')) {
                                                    await api.delete(`/superadmin/tenants/${tenantId}/cart`);
                                                    queryClient.invalidateQueries({ queryKey: ['tenant-cart', tenantId] });
                                                }
                                            }}>
                                            Delete Cart
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </Box>

                        {cartLoading ? (
                            <CircularProgress size={24} />
                        ) : !cartData ? (
                            <Box>
                                <Typography color="text.secondary" sx={{ mb: 2 }}>No cart exists for this tenant.</Typography>
                                <Button variant="contained" onClick={async () => {
                                    await api.post(`/superadmin/tenants/${tenantId}/cart`, {});
                                    queryClient.invalidateQueries({ queryKey: ['tenant-cart', tenantId] });
                                }}>
                                    Create Cart
                                </Button>
                            </Box>
                        ) : (
                            <Box>
                                {/* Cart Plan */}
                                <Card sx={{ mb: 2, p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>Selected Plan</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel>Plan</InputLabel>
                                            <Select
                                                value={cartData.plan_id || ''}
                                                label="Plan"
                                                onChange={async (e) => {
                                                    await api.post(`/superadmin/tenants/${tenantId}/cart`, { plan_id: e.target.value || null });
                                                    queryClient.invalidateQueries({ queryKey: ['tenant-cart', tenantId] });
                                                }}
                                            >
                                                <MenuItem value="">No Plan</MenuItem>
                                                {plansData?.map((plan: any) => (
                                                    <MenuItem key={plan.id} value={plan.id}>{plan.name} - ₹{plan.price}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {cartData.plan && <Typography>₹{cartData.plan.price}</Typography>}
                                    </Box>
                                </Card>

                                {/* Cart Total */}
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Total: ₹{cartData.total_amount || 0}
                                </Typography>

                                {/* Cart Items */}
                                <Typography variant="subtitle1" gutterBottom>Addon Items</Typography>
                                {cartData.items?.length > 0 ? (
                                    <Box sx={{ mb: 2 }}>
                                        {cartData.items.map((item: any) => (
                                            <Card key={item.id} sx={{ mb: 1, p: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                    <Box>
                                                        <Typography fontWeight="medium">{item.addon?.name || 'Unknown Addon'}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            ₹{item.unit_price} x {item.quantity} = ₹{item.total_price}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={item.quantity}
                                                            sx={{ width: 80 }}
                                                            inputProps={{ min: 1 }}
                                                            onChange={async (e) => {
                                                                const qty = parseInt(e.target.value) || 1;
                                                                await api.put(`/superadmin/tenants/${tenantId}/cart/items/${item.id}`, { quantity: qty });
                                                                queryClient.invalidateQueries({ queryKey: ['tenant-cart', tenantId] });
                                                            }}
                                                        />
                                                        <IconButton color="error" onClick={async () => {
                                                            if (window.confirm('Remove this item?')) {
                                                                await api.delete(`/superadmin/tenants/${tenantId}/cart/items/${item.id}`);
                                                                queryClient.invalidateQueries({ queryKey: ['tenant-cart', tenantId] });
                                                            }
                                                        }}>
                                                            <Delete />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            </Card>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography color="text.secondary" sx={{ mb: 2 }}>No addon items in cart.</Typography>
                                )}

                                {/* Add Item */}
                                <Button variant="outlined" startIcon={<Add />} onClick={() => {
                                    setCartAddonForm({ addon_id: '', quantity: 1 });
                                    setCartAddonDialogOpen(true);
                                }}>
                                    Add Addon Item
                                </Button>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Constituencies Tab */}
                    <TabPanel value={tabValue} index={3}>
                        <ConstituenciesTab tenantId={tenantId!} />
                    </TabPanel>
                </CardContent>
            </Card>

            {/* User Dialog */}
            <Dialog open={userDialogOpen} onClose={closeUserDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedUser ? 'Edit User' : 'Add User'}</DialogTitle>
                <form onSubmit={(e) => { e.preventDefault(); saveUserMutation.mutate(userForm); }}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <TextField label="Name" fullWidth required value={userForm.name}
                            onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} sx={{ mb: 2 }} />
                        <TextField label="Phone" fullWidth required value={userForm.phone}
                            onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} sx={{ mb: 2 }} />
                        <TextField label="Email" type="email" fullWidth value={userForm.email}
                            onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} sx={{ mb: 2 }} />
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select value={userForm.role} label="Role"
                                onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}>
                                <MenuItem value="admin">Admin</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={closeUserDialog}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveUserMutation.isPending}>
                            {saveUserMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Subscription Dialog */}
            <Dialog open={subscriptionDialogOpen} onClose={closeSubscriptionDialog} maxWidth="md" fullWidth fullScreen={isMobile}>
                <DialogTitle fontSize={22}>{selectedSubscription ? 'Edit Subscription' : 'Add Subscription'}</DialogTitle>
                <form onSubmit={(e) => { e.preventDefault(); saveSubscriptionMutation.mutate(subForm); }}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Plan *</InputLabel>
                            <Select value={subForm.plan_id} label="Plan *" required
                                onChange={(e) => {
                                    const plan = plansData?.find((obj: any) => obj.id === e.target.value);
                                    setSubForm({
                                        ...subForm, plan_id: e.target.value,
                                        start_date: new Date().toISOString().split('T')[0],
                                        end_date: new Date(Date.now() + plan?.duration_days * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                                        max_citizens: plan?.max_citizens,
                                        max_users: plan?.max_users,
                                        max_storage: plan?.max_storage,
                                        is_active: true,
                                        has_cms_access: true,
                                        has_campaign_access: true,
                                    })
                                }}>
                                {plansData?.map((plan: any) => (
                                    <MenuItem key={plan.id} value={plan.id}>{plan.name} - ₹{plan.price}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField label="Start Date *" type="date" fullWidth required
                                value={subForm.start_date} InputLabelProps={{ shrink: true }}
                                onChange={(e) => setSubForm({ ...subForm, start_date: e.target.value })} />
                            <TextField label="End Date *" type="date" fullWidth required
                                value={subForm.end_date} InputLabelProps={{ shrink: true }}
                                onChange={(e) => setSubForm({ ...subForm, end_date: e.target.value })} />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
                            <TextField label="Max Citizens *" type="number" fullWidth required
                                value={subForm.max_citizens} inputProps={{ min: 1 }}
                                onChange={(e) => setSubForm({ ...subForm, max_citizens: parseInt(e.target.value) || 1 })} />
                            <TextField label="Max Users" type="number" fullWidth
                                value={subForm.max_users} inputProps={{ min: 1 }}
                                onChange={(e) => setSubForm({ ...subForm, max_users: parseInt(e.target.value) || 1 })} />
                            <TextField label="Max Storage (MB)" type="number" fullWidth
                                value={subForm.max_storage} inputProps={{ min: 1 }}
                                onChange={(e) => setSubForm({ ...subForm, max_storage: parseInt(e.target.value) || 1 })} />
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <FormControlLabel control={<Switch checked={subForm.is_active}
                                onChange={(e) => setSubForm({ ...subForm, is_active: e.target.checked })} />} label="Active" />
                            <FormControlLabel control={<Switch checked={subForm.has_cms_access}
                                onChange={(e) => setSubForm({ ...subForm, has_cms_access: e.target.checked })} />} label="CMS Access" />
                            <FormControlLabel control={<Switch checked={subForm.has_campaign_access}
                                onChange={(e) => setSubForm({ ...subForm, has_campaign_access: e.target.checked })} />} label="Campaign Access" />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button variant="outlined" onClick={closeSubscriptionDialog}>Cancel</Button>
                        <Button type="submit" color="success" variant="contained" disabled={saveSubscriptionMutation.isPending}>
                            {saveSubscriptionMutation.isPending ? 'Saving...' : 'Save'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Cart Addon Dialog */}
            <Dialog open={cartAddonDialogOpen} onClose={() => setCartAddonDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add Addon to Cart</DialogTitle>
                <form onSubmit={async (e) => {
                    e.preventDefault();
                    try {
                        await api.post(`/superadmin/tenants/${tenantId}/cart/items`, {
                            addon_id: parseInt(cartAddonForm.addon_id),
                            quantity: cartAddonForm.quantity
                        });
                        queryClient.invalidateQueries({ queryKey: ['tenant-cart', tenantId] });
                        setCartAddonDialogOpen(false);
                    } catch (err: any) {
                        setError(err.response?.data?.message || 'Failed to add item');
                    }
                }}>
                    <DialogContent>
                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Addon *</InputLabel>
                            <Select
                                value={cartAddonForm.addon_id}
                                label="Addon *"
                                required
                                onChange={(e) => setCartAddonForm({ ...cartAddonForm, addon_id: e.target.value })}
                            >
                                {addonsData?.map((addon: any) => (
                                    <MenuItem key={addon.id} value={addon.id}>
                                        {addon.name} ({addon.type}) - ₹{addon.price}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Quantity"
                            type="number"
                            fullWidth
                            required
                            value={cartAddonForm.quantity}
                            onChange={(e) => setCartAddonForm({ ...cartAddonForm, quantity: parseInt(e.target.value) || 1 })}
                            inputProps={{ min: 1 }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setCartAddonDialogOpen(false)}>Cancel</Button>
                        <Button type="submit" variant="contained">Add</Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete User Confirmation Dialog */}
            <Dialog open={deleteUserDialogOpen} onClose={() => setDeleteUserDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete <strong>"{userToDelete?.name}"</strong>?</Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Warning: You will not be able to revert this action!</Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={() => setDeleteUserDialogOpen(false)} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={() => {
                        if (userToDelete) deleteUserMutation.mutate(userToDelete.id);
                        setDeleteUserDialogOpen(false);
                        setUserToDelete(null);
                    }} disabled={deleteUserMutation.isPending} startIcon={<Delete />}
                        sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteUserMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Subscription Confirmation Dialog */}
            <Dialog open={deleteSubscriptionDialogOpen} onClose={() => setDeleteSubscriptionDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
                <Box sx={{ bgcolor: '#fee2e2', p: 3, display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #fecaca' }}>
                    <Box sx={{ bgcolor: '#dc2626', borderRadius: '50%', p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Delete sx={{ color: 'white', fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b' }}>Confirm Delete</Typography>
                        <Typography variant="body2" sx={{ color: '#b91c1c' }}>This action cannot be undone</Typography>
                    </Box>
                </Box>
                <DialogContent sx={{ p: 3 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>Are you sure you want to delete subscription <strong>#{subscriptionToDelete?.id}</strong>?</Typography>
                    <Alert severity="warning" sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', '& .MuiAlert-icon': { color: '#d97706' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Warning: You will not be able to revert this action!</Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1, bgcolor: '#f9fafb' }}>
                    <Button variant="outlined" onClick={() => setDeleteSubscriptionDialogOpen(false)} sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={() => {
                        if (subscriptionToDelete) deleteSubscriptionMutation.mutate(subscriptionToDelete.id);
                        setDeleteSubscriptionDialogOpen(false);
                        setSubscriptionToDelete(null);
                    }} disabled={deleteSubscriptionMutation.isPending} startIcon={<Delete />}
                        sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}>
                        {deleteSubscriptionMutation.isPending ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box >
    );
};

// Constituencies Tab Component
const ConstituenciesTab = ({ tenantId }: { tenantId: string }) => {
    const queryClient = useQueryClient();
    const [error, setError] = useState('');
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedConstituency, setSelectedConstituency] = useState<any>(null);
    const [assigning, setAssigning] = useState(false);

    // Fetch assigned constituencies
    const { data: assignedData, isLoading: assignedLoading } = useQuery({
        queryKey: ['tenant-constituencies', tenantId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}/constituencies`);
            return res.data?.data || [];
        },
        refetchInterval: (query) => {
            const data = query.state.data;
            if (Array.isArray(data)) {
                const hasActive = data.some((item: any) =>
                    item.import_status === 'processing' || item.import_status === 'pending'
                );
                if (hasActive) return 2000;
            }
            return false;
        }
    });

    // Fetch available constituencies
    const { data: availableData, isLoading: availableLoading } = useQuery({
        queryKey: ['available-constituencies', tenantId],
        queryFn: async () => {
            const res = await api.get(`/superadmin/tenants/${tenantId}/constituencies/available`);
            return res.data?.data || [];
        },
    });

    const handleAssign = async () => {
        if (!selectedConstituency) return;
        setAssigning(true);
        setError('');
        try {
            await api.post(`/superadmin/tenants/${tenantId}/constituencies`, {
                master_constituency_id: selectedConstituency.id,
            });
            queryClient.invalidateQueries({ queryKey: ['tenant-constituencies', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['available-constituencies', tenantId] });
            setAssignDialogOpen(false);
            setSelectedConstituency(null);
        } catch (err: any) {
            const msg = err.response?.data?.message || 'Failed to assign constituency';
            const details = err.response?.data;
            if (details?.current_count !== undefined) {
                setError(`${msg}\n\nCurrent: ${details.current_count} | To Add: ${details.citizens_to_add} | Limit: ${details.effective_limit} | Available: ${details.available_slots}`);
            } else {
                setError(msg);
            }
        } finally {
            setAssigning(false);
        }
    };

    const handleUnassign = async (constituencyId: number) => {
        if (!window.confirm('Are you sure you want to remove this constituency? All related tenant data will be deleted.')) return;
        try {
            await api.delete(`/superadmin/tenants/${tenantId}/constituencies/${constituencyId}`);
            queryClient.invalidateQueries({ queryKey: ['tenant-constituencies', tenantId] });
            queryClient.invalidateQueries({ queryKey: ['available-constituencies', tenantId] });
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to remove constituency');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Assigned Constituencies</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => setAssignDialogOpen(true)}>
                    Assign Constituency
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError('')}>{error}</Alert>}

            {assignedLoading ? (
                <CircularProgress size={24} />
            ) : assignedData?.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {assignedData.map((item: any) => {
                        const isProcessing = item.import_status === 'processing' || item.import_status === 'pending';
                        const isFailed = item.import_status === 'failed';
                        const progress = item.import_progress || 0;

                        return (
                            <Card key={item.id} sx={{ p: 2, borderLeft: isProcessing ? '4px solid #1976d2' : isFailed ? '4px solid #d32f2f' : 'none' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography fontWeight="bold">{item.name}</Typography>
                                            {isProcessing && <Chip size="small" label="Importing..." color="primary" variant="outlined" />}
                                            {isFailed && <Chip size="small" label="Import Failed" color="error" variant="outlined" />}
                                        </Box>

                                        {isProcessing ? (
                                            <Box sx={{ mt: 1, width: '100%', maxWidth: 300 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    Copying data... {progress}%
                                                </Typography>
                                                <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                                            </Box>
                                        ) : isFailed ? (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                                {item.import_error || 'Unknown error occurred'}
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                                <Chip size="small" label={`${item.villages_count || 0} Villages`} color="primary" variant="outlined" />
                                                <Chip size="small" label={`${item.voting_booths_count || 0} Booths`} color="secondary" variant="outlined" />
                                                <Chip size="small" label={`${item.citizens_count || 0} Citizens`} color="success" variant="outlined" />
                                            </Box>
                                        )}
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<Delete />}
                                        onClick={() => handleUnassign(item.id)}
                                        disabled={isProcessing}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            </Card>
                        );
                    })}
                </Box>
            ) : (
                <Typography color="text.secondary">No constituencies assigned to this tenant.</Typography>
            )}

            {/* Assign Dialog */}
            <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign Constituency</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a master constituency to assign. All villages, booths, and citizens will be duplicated to this tenant.
                    </Typography>
                    {availableLoading ? (
                        <CircularProgress size={24} />
                    ) : availableData?.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {availableData.map((item: any) => (
                                <Card
                                    key={item.id}
                                    sx={{
                                        p: 2,
                                        cursor: 'pointer',
                                        border: selectedConstituency?.id === item.id ? '2px solid' : '1px solid',
                                        borderColor: selectedConstituency?.id === item.id ? 'primary.main' : 'divider',
                                        bgcolor: selectedConstituency?.id === item.id ? 'action.selected' : 'background.paper',
                                    }}
                                    onClick={() => setSelectedConstituency(item)}
                                >
                                    <Typography fontWeight="medium">{item.name}</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                        <Chip size="small" label={`${item.villages_count || 0} Villages`} />
                                        <Chip size="small" label={`${item.citizens_count || 0} Citizens`} color="warning" />
                                    </Box>
                                </Card>
                            ))}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">No available constituencies to assign.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button variant="outlined" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleAssign}
                        disabled={!selectedConstituency || assigning}
                    >
                        {assigning ? 'Assigning...' : 'Assign'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TenantDetail;
